import React, { useMemo } from 'react';
import { Avatar, Box, Link, Skeleton, Tooltip, Typography } from '@mui/material';
import { History, User } from 'lucide-react';
import { FloorplanActivityEntry, FloorplanLevel } from '../../types/floorplan';
import { EmptyState, ErrorState, PersonAvatar, SectionCard } from '../ui';
import { useDelayedFlag } from '../ui/useDelayedFlag';
import { sbg } from '../../theme/sbgTokens';
import { formatCount, formatDate, formatDateTime, formatRelative, formatTime } from '../../utils/format';
import { addDaysToYmd, EASTERN_TIME_ZONE, getTodayEt } from '../../utils/easternTime';
import { deskInSentence, deskNumberOf, floorNameByDeskId } from './floorplan-panels/deskLabel';

interface ActivityFeedProps {
  entries: FloorplanActivityEntry[];
  /** Optional: the layout's floors, so a desk on a multi-floor layout reads "desk 54 on 4th Floor". */
  levels?: FloorplanLevel[];
  /** Optional: the feed is loading (skeleton rows after 300ms instead of the empty state). */
  loading?: boolean;
  /** Optional: the last load failed. With no entries this shows an error with Retry. */
  error?: boolean;
  onRetry?: () => void;
}

const FEED_CONTAINER = 'floorplan-activity';

const ET_DAY_KEY = new Intl.DateTimeFormat('en-CA', {
  timeZone: EASTERN_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** The Eastern calendar day an entry belongs to, as YYYY-MM-DD. */
const dayKeyOf = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : ET_DAY_KEY.format(date);
};

const dayLabelOf = (key: string, todayKey: string) => {
  if (!key) return 'Earlier';
  if (key === todayKey) return 'Today';
  if (key === addDaysToYmd(todayKey, -1)) return 'Yesterday';
  return formatDate(key, { short: key.slice(0, 4) === todayKey.slice(0, 4) });
};

/** The object of a sentence: the person, desk or version the change was about. */
const Strong: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 500, color: sbg.text.primary }}>
    {children}
  </Box>
);

const detailString = (entry: FloorplanActivityEntry, key: string) => {
  const value = entry.details?.[key];
  return typeof value === 'string' && value.trim() ? value : null;
};

const detailNumber = (entry: FloorplanActivityEntry, key: string) => {
  const value = entry.details?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

/** "assigned Maria Chen to desk 14": the predicate that follows the actor's name. */
const describe = (entry: FloorplanActivityEntry, floorName: string | null): React.ReactNode => {
  const employee = <Strong>{entry.employee?.name || 'an employee'}</Strong>;
  // Desks are named by the number on the canvas, never the internal key.
  const desk = deskNumberOf(entry.desk)
    ? <Strong>{`${deskInSentence(entry.desk)}${floorName ? ` on ${floorName}` : ''}`}</Strong>
    : 'a desk';
  const version = entry.version?.name ? <Strong>{entry.version.name}</Strong> : 'a version';

  switch (entry.activityType) {
    case 'create_assignment':
      return <>assigned {employee} to {desk}</>;
    case 'move_assignment':
      return <>moved {employee} to {desk}</>;
    case 'swap_assignment':
      return <>swapped {employee} into {desk}</>;
    case 'replace_assignment':
      return <>assigned {employee} to {desk}, replacing its previous occupant</>;
    case 'clear_assignment':
      return entry.employee?.name ? <>removed {employee} from {desk}</> : <>cleared {desk}</>;
    case 'clear_level': {
      const count = detailNumber(entry, 'clearedCount');
      const floor = detailString(entry, 'levelName');
      return (
        <>
          cleared {count !== null ? formatCount(count, 'desk') : 'every desk'} on {floor ? <Strong>{floor}</Strong> : 'a floor'}
        </>
      );
    }
    case 'batch_assignment':
      return <>applied a batch of desk changes</>;
    case 'team_group_move': {
      const team = detailString(entry, 'teamName');
      return <>moved {team ? <Strong>{team}</Strong> : 'a team'} to nearby desks</>;
    }
    case 'smart_assign_apply': {
      const team = detailString(entry, 'teamName');
      return team ? <>auto-assigned desks for <Strong>{team}</Strong></> : <>auto-assigned desks for every team</>;
    }
    case 'create_version':
      return <>created {version}</>;
    case 'activate_version':
      return <>made {version} the active version</>;
    case 'edit_geometry': {
      const floor = detailString(entry, 'name');
      return <>updated the layout of {floor ? <Strong>{floor}</Strong> : 'a floor'}</>;
    }
    default:
      return <>updated the floor plan</>;
  }
};

const isVersionEvent = (type: string) => type === 'create_version' || type === 'activate_version';

/** Relative time reads as "today" only up to ~23h; past that it would round to "Yesterday". */
const RELATIVE_LIMIT_MS = 23 * 60 * 60 * 1000;

const ActivityRow: React.FC<{ entry: FloorplanActivityEntry; now: Date; isToday: boolean; floorName: string | null }> = ({
  entry,
  now,
  isToday,
  floorName,
}) => {
  const actorName = entry.actor?.fullName?.trim() || null;
  const absolute = formatDateTime(entry.createdAt);
  const showVersion = entry.version?.name && !isVersionEvent(entry.activityType);
  // The day header already names the day, so rows show the clock time; only Today's rows use
  // relative time ("12m ago"), which can't contradict the header.
  const age = now.getTime() - new Date(entry.createdAt).getTime();
  const shown = isToday && age >= 0 && age < RELATIVE_LIMIT_MS
    ? formatRelative(entry.createdAt, now)
    : formatTime(entry.createdAt);

  return (
    <Box
      component="li"
      sx={{
        display: 'grid',
        gridTemplateColumns: '32px minmax(0, 1fr)',
        columnGap: 1.5,
        rowGap: 0.25,
        alignItems: 'start',
        py: 1.25,
        '&:not(:last-of-type)': { borderBottom: `1px solid ${sbg.border.subtle}` },
        [`@container ${FEED_CONTAINER} (min-width: 640px)`]: {
          gridTemplateColumns: '32px minmax(0, 1fr) auto',
          alignItems: 'center',
        },
      }}
    >
      {actorName ? (
        <PersonAvatar name={actorName} size={32} />
      ) : (
        <Avatar aria-hidden="true" sx={{ width: 32, height: 32, bgcolor: sbg.bg.avatar, color: sbg.text.avatar }}>
          <User size={16} />
        </Avatar>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary, overflowWrap: 'anywhere' }}>
          <Strong>{actorName || 'An admin'}</Strong> {describe(entry, floorName)}
        </Typography>
        {showVersion && (
          <Typography variant="caption" component="p" sx={{ color: sbg.text.secondary }}>
            {`In ${entry.version?.name}`}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          gridColumn: '2',
          [`@container ${FEED_CONTAINER} (min-width: 640px)`]: { gridColumn: '3', gridRow: '1', textAlign: 'right' },
        }}
      >
        <Tooltip title={absolute} placement="top-end">
          <Typography
            variant="caption"
            component="time"
            dateTime={entry.createdAt}
            sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
          >
            {shown}
          </Typography>
        </Tooltip>
        <span className="sbg-sr-only">{`, ${absolute}`}</span>
      </Box>
    </Box>
  );
};

const ActivityFeed: React.FC<ActivityFeedProps> = ({ entries, levels, loading = false, error = false, onRetry }) => {
  const showSkeleton = useDelayedFlag(loading && entries.length === 0);
  const now = new Date();
  const todayKey = getTodayEt();
  const floorByDeskId = useMemo(() => floorNameByDeskId(levels), [levels]);

  const groups = useMemo(() => {
    const result: { key: string; entries: FloorplanActivityEntry[] }[] = [];
    entries.forEach((entry) => {
      const key = dayKeyOf(entry.createdAt);
      const last = result[result.length - 1];
      if (last && last.key === key) {
        last.entries.push(entry);
      } else {
        result.push({ key, entries: [entry] });
      }
    });
    return result;
  }, [entries]);

  const renderBody = () => {
    if (entries.length === 0 && loading) {
      return (
        <Box role="status" aria-label="Loading recent activity…" sx={{ visibility: showSkeleton ? 'visible' : 'hidden' }}>
          {[0, 1, 2, 3].map((row) => (
            <Box key={row} aria-hidden="true" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25 }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="rounded" width={`${55 + ((row * 17) % 30)}%`} height={14} />
                <Skeleton variant="rounded" width={64} height={12} sx={{ mt: 0.75 }} />
              </Box>
            </Box>
          ))}
        </Box>
      );
    }

    if (entries.length === 0 && error) {
      return (
        <ErrorState
          compact
          title="Couldn’t load recent activity"
          body="The server didn’t respond. The floor plan itself is unaffected; try again in a minute."
          onRetry={onRetry}
        />
      );
    }

    if (entries.length === 0) {
      return (
        <EmptyState
          compact
          icon={<History />}
          title="No changes yet"
          body="Desk assignments, moves and version changes on this layout appear here."
        />
      );
    }

    return (
      <Box sx={{ maxHeight: 360, overflowY: 'auto', mx: -0.5, px: 0.5 }}>
        {error && (
          <Typography variant="caption" component="p" sx={{ color: sbg.status.warning.text, pb: 1 }}>
            Couldn’t refresh activity. Showing the last changes that loaded.
            {onRetry && (
              <>
                {' '}
                <Link component="button" type="button" variant="caption" onClick={onRetry} sx={{ verticalAlign: 'baseline' }}>
                  Try again
                </Link>
              </>
            )}
          </Typography>
        )}
        {groups.map((group) => (
          <Box component="section" key={group.key || 'unknown'} aria-label={dayLabelOf(group.key, todayKey)} sx={{ '& + &': { mt: 1.5 } }}>
            <Typography
              variant="overline"
              component="h3"
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                py: 0.5,
                bgcolor: sbg.bg.surface,
                color: sbg.text.secondary,
              }}
            >
              {dayLabelOf(group.key, todayKey)}
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {group.entries.map((entry) => (
                <ActivityRow
                  key={entry.id}
                  entry={entry}
                  now={now}
                  isToday={group.key === todayKey}
                  floorName={entry.desk ? floorByDeskId.get(entry.desk.id) ?? null : null}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <SectionCard
      id="floorplan-activity"
      title="Recent activity"
      description="Desk and version changes on this layout, newest first"
      sx={{ height: '100%', containerType: 'inline-size', containerName: FEED_CONTAINER }}
    >
      {renderBody()}
    </SectionCard>
  );
};

export default ActivityFeed;
